import React, {
  useRef,
  useCallback,
  useEffect,
  useMemo,
} from 'react';
import { css } from '@emotion/core';
import { debounce } from 'lodash';
import styled from '@emotion/styled';

import useGrid from './useGrid';
import Item from './Item';

import { refreshingSubtle } from '~/theseus/styles/keyframes';

const loadingStyles = ({ isLoading }) => {
  if (isLoading) {
    return css`
      animation: ${refreshingSubtle} 1s linear infinite;
      pointer-events: none;
    `;
  }

  return css``;
};

const GridInner = styled.ul`
  position: relative;
  display: grid;
  grid-template-columns: repeat(${props => props.columns || 2}, 1fr);
  gap: 20px;
  overflow: scroll;
  height: ${props => props.height || 360}px;
  cursor: default;
  background: #333;
  scroll-behavior: smooth;
  padding: 15px;

  // This is a hack to get padding on the bottom of the grid
  // for overflow scrolling
  .bottom-padding {
    grid-column: 1 / ${props => props.columns || 2};
    height: 0.1px;
  }

  ${loadingStyles}
`;

// If the Set size is equal to the original id array length, then we know for certain that all of the ids are unique
const containsUniqIds = ids => ids.length === new Set(ids).size;

export default function Grid(props) {
  const {
    label,
    idPrefix = 'grid',
    columns = 2,
    isLoading = false,
    selected,
    onChange,
    children,
    ...rest
  } = props;

  const listRef = useRef(null);
  /* Produces an array of item ids from the children of the Grid.
   * If the children of the Grid do not have unique ids, the ids are not numbers or strings,
   * or the children do not contain ids then an Error will be thrown
   */
  const itemIds = useMemo(() => {
    if (!children) {
      return [];
    }

    const ids = React.Children.map(children, (child) => {
      if (child.props.id === undefined || child.props.id === null) {
        throw new Error(`Each child of a Grid must have an id: ${idPrefix}`);
      }

      if (
        typeof child.props.id !== 'number'
        && typeof child.props.id !== 'string'
      ) {
        throw new Error(
          `Ids of grid children must be string or number: ${idPrefix}`,
        );
      }

      return child.props.id;
    });

    if (!containsUniqIds(ids)) {
      throw new Error(`Children of grid must have unique ids: ${idPrefix}`);
    }

    return ids;
  }, [children, idPrefix]);

  const state = useGrid(itemIds, { columns, selected, idPrefix });

  // If the user is rapidly pressing the arrow keys, this will prevent the scrolling animation from stuttering
  const debouncedScroll = useCallback(debounce((offset) => {
    if (listRef.current) {
      listRef.current.scrollTop = offset;
    }
  }, 10), []);

  const modifiedChildren = useMemo(
    () => React.Children.map(children, child => React.cloneElement(child, {
      key: `${state.id}-${child.props.id}`,
      isSelected: state.selected === child.props.id,
      selectItem: state.selectItem,
      gridId: state.id,
      scrollTo: debouncedScroll,
    })),
    [children, debouncedScroll, state.selected, state.selectItem, state.id],
  );

  // In order to be aria compliant, we need to ensure that all Grids have an associated label
  useEffect(() => {
    if (!label) {
      throw Error(`All Grids must have a label: ${idPrefix}`);
    }
  }, [label, idPrefix]);

  useEffect(() => {
    if (!isLoading) {
      onChange(state.selected);
    }
  }, [onChange, isLoading, state.selected]);

  return (
    <GridInner
      role="listbox"
      tabIndex="0"
      onKeyDown={state.handleKeyPress}
      columns={state.columns}
      aria-label={label}
      aria-activedescendant={
        state.selected ? `${state.id}-${state.selected}` : null
      }
      isLoading={isLoading}
      aria-busy={!!isLoading}
      aria-live="polite"
      ref={listRef}
      {...rest}
    >
      {modifiedChildren}
      <div aria-hidden="true" className="bottom-padding" />
    </GridInner>
  );
}

Grid.Item = Item;
